import './CreatePromoLink.sass';

import React, { FC, useEffect } from 'react';
import { Button, Form, FormItem, Header, Input, ScrollView } from '$uikit';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute } from 'react-router5';
import {
  promoLinksActions,
  promoLinksFormSelector,
  promoLinksSelector,
} from '$store/promoLinks';
import { useTranslator } from '$hooks';
import { toastActions } from '$store/toast';
import { PromoLinksState } from '$store/promoLinks/interface';

// Строчные буквы и цифры, без дефисов: в токене "<slug>-<rand6>"
const SLUG_REGEX = /^[a-z0-9]{2,32}$/;

export const CreatePromoLink: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { route } = useRoute();
  const form = useSelector(promoLinksFormSelector);
  const { editingId, links } = useSelector(promoLinksSelector);
  const editLinkId = Number(route?.params?.editLinkId) || 0;
  const isEdit = editingId !== null || editLinkId > 0;

  useEffect(() => {
    if (editLinkId && !links.some((l) => l.id === editLinkId)) {
      dispatch(promoLinksActions.loadLinks());
    }
    return () => {
      dispatch(promoLinksActions.clearForm());
    };
  }, []);

  useEffect(() => {
    if (!editLinkId || editingId === editLinkId) {
      return;
    }
    const link = links.find((l) => l.id === editLinkId);
    if (link) {
      dispatch(promoLinksActions.startEdit(link));
    }
  }, [editLinkId, editingId, links, dispatch]);

  const handleChange =
    (name: keyof PromoLinksState['form']) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      dispatch(promoLinksActions.setFormField({ name, value: e.target.value }));
    };

  const handleSlugChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .slice(0, 32);
    dispatch(promoLinksActions.setFormField({ name: 'slug', value }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      dispatch(
        toastActions.setToastFail(
          t('promo_links_toast_name_required', { defaultValue: 'Enter a name' }),
        ),
      );
      return;
    }
    if (form.name.length > 64) {
      dispatch(
        toastActions.setToastFail(
          t('promo_links_toast_name_length', { defaultValue: 'Name is too long' }),
        ),
      );
      return;
    }

    if (!isEdit && (!SLUG_REGEX.test(form.slug) || form.slug.startsWith('link'))) {
      dispatch(
        toastActions.setToastFail(
          t('promo_links_toast_slug_invalid', {
            defaultValue:
              'Campaign: 2-32 lowercase letters or digits, cannot start with "link"',
          }),
        ),
      );
      return;
    }
    if (form.description.length > 512) {
      dispatch(
        toastActions.setToastFail(
          t('promo_links_toast_description_length', {
            defaultValue: 'Description is too long',
          }),
        ),
      );
      return;
    }
    dispatch(
      isEdit ? promoLinksActions.editPromoLink() : promoLinksActions.createPromoLink(),
    );
  };

  return (
    <>
      <Header showCloseButton>
        {isEdit
          ? t('promo_links_form_title_edit', { defaultValue: 'Edit link' })
          : t('promo_links_form_title', { defaultValue: 'New UTM link' })}
      </Header>
      <ScrollView
        footer={
          <Button onClick={handleSubmit}>
            {isEdit
              ? t('promo_links_form_button_save', { defaultValue: 'Save' })
              : t('promo_links_form_button', { defaultValue: 'Create' })}
          </Button>
        }
      >
        <Form>
          <FormItem
            className={isEdit ? 'CreatePromoLink__slugLocked' : undefined}
            title={t('promo_links_form_slug', { defaultValue: 'Campaign (slug)' })}
            description={
              isEdit
                ? undefined
                : t('promo_links_form_slug_desc', {
                    defaultValue:
                      'Lowercase letters and digits, 2-32 chars. Used as utm_campaign',
                  })
            }
          >
            <Input
              value={form.slug}
              onChange={handleSlugChange}
              placeholder="instabio"
              disabled={isEdit}
            />
          </FormItem>
          <FormItem
            title={t('promo_links_form_name', { defaultValue: 'Name' })}
            description={
              <span className="CreatePromoLink__nameDesc">
                <span>
                  {t('promo_links_form_name_desc', {
                    defaultValue: 'Visible only to you',
                  })}
                </span>
                <span>{`${form.name.length}/64`}</span>
              </span>
            }
          >
            <Input
              value={form.name}
              onChange={handleChange('name')}
              maxLength={64}
              placeholder={t('promo_links_form_name_placeholder', {
                defaultValue: 'Instagram bio',
              })}
            />
          </FormItem>
          <FormItem
            title={t('promo_links_form_description', {
              defaultValue: 'Description (optional)',
            })}
            description={`${form.description.length}/512`}
            descriptionAlign="right"
          >
            <Input
              value={form.description}
              onChange={handleChange('description')}
              placeholder={t('promo_links_form_description_placeholder', {
                defaultValue: 'Where this link is placed',
              })}
            />
          </FormItem>
        </Form>
      </ScrollView>
    </>
  );
};
